import React, { cloneElement } from 'react';
import { CreditCardIcon, ScanIcon, WifiIcon, CheckCircleIcon, SmartphoneIcon, SettingsIcon, QrCodeIcon, AppleIcon, ArrowRightIcon } from 'lucide-react';
const steps = [{
  id: 1,
  icon: <CreditCardIcon className="h-8 w-8" data-id="element-448" />,
  title: 'Choose your plan',
  description: 'Select the eSIM data plan that matches your destination and needs.'
}, {
  id: 2,
  icon: <ScanIcon className="h-8 w-8" data-id="element-449" />,
  title: 'Pay securely',
  description: 'Complete your purchase using our secure payment methods.'
}, {
  id: 3,
  icon: <WifiIcon className="h-8 w-8" data-id="element-450" />,
  title: 'Scan QR or install manually',
  description: 'Activate your eSIM by scanning the QR code or using the installation instructions.'
}, {
  id: 4,
  icon: <CheckCircleIcon className="h-8 w-8" data-id="element-451" />,
  title: "You're online!",
  description: 'Connect to local networks and enjoy high-speed data during your travels.'
}];
const iphoneSteps = [{
  id: 1,
  title: 'Open Settings',
  description: 'Navigate to the Settings app on your iPhone',
  image: 'https://images.unsplash.com/photo-1584006682522-dc17d6c0d9ac?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <SettingsIcon className="h-6 w-6" data-id="element-452" />
}, {
  id: 2,
  title: 'Tap Cellular/Mobile Data',
  description: 'Select Cellular or Mobile Data from the menu',
  image: 'https://images.unsplash.com/photo-1585060544812-6b45742d762f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <SmartphoneIcon className="h-6 w-6" data-id="element-453" />
}, {
  id: 3,
  title: 'Add Cellular/Mobile Plan',
  description: 'Tap on "Add Cellular Plan" or "Add Mobile Plan"',
  image: 'https://images.unsplash.com/photo-1580910051074-3eb694886505?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <QrCodeIcon className="h-6 w-6" data-id="element-454" />
}, {
  id: 4,
  title: 'Scan QR Code',
  description: 'Scan the QR code we provide after purchase',
  image: 'https://images.unsplash.com/photo-1613758235402-745466bb7ded?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <ScanIcon className="h-6 w-6" data-id="element-455" />
}];
const androidSteps = [{
  id: 1,
  title: 'Open Settings',
  description: 'Navigate to the Settings app on your Android device',
  image: 'https://images.unsplash.com/photo-1598327105666-5b89351aff97?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <SettingsIcon className="h-6 w-6" data-id="element-456" />
}, {
  id: 2,
  title: 'Network & Internet',
  description: 'Tap on "Network & Internet" or "Connections"',
  image: 'https://images.unsplash.com/photo-1592899677977-9c10ca588bbd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <WifiIcon className="h-6 w-6" data-id="element-457" />
}, {
  id: 3,
  title: 'Mobile Network/SIMs',
  description: 'Select "Mobile Network", "SIMs" or "SIM Card Manager"',
  image: 'https://images.unsplash.com/photo-1601784551446-20c9e07cdbdb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <SmartphoneIcon className="h-6 w-6" data-id="element-458" />
}, {
  id: 4,
  title: 'Add eSIM',
  description: 'Tap "Add eSIM", "Download a SIM" or "+" icon, then scan QR code',
  image: 'https://images.unsplash.com/photo-1575718120842-54e388fc35f7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80',
  icon: <QrCodeIcon className="h-6 w-6" data-id="element-459" />
}];
export function HowItWorks() {
  return <section id="how-it-works" className="py-16 w-full bg-[#F9FAFB]" data-id="element-460">
      <div className="container mx-auto px-4" data-id="element-461">
        <div className="text-center mb-12" data-id="element-462">
          <h2 className="text-3xl font-bold mb-4" data-id="element-463">How It Works</h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-464">
            Setting up your travel eSIM is quick and easy. Follow these simple
            steps to get connected.
          </p>
        </div>
        {/* Desktop view - horizontal steps */}
        <div className="hidden md:grid grid-cols-4 gap-8 relative" data-id="element-465">
          {/* Connection line */}
          <div className="absolute top-16 left-0 w-full h-0.5 bg-gray-200" aria-hidden="true" data-id="element-466"></div>
          {steps.map(step => <div key={step.id} className="relative flex flex-col items-center" data-id="element-467">
              {/* Step number */}
              <div className="w-12 h-12 rounded-full bg-[#2563EB] text-white flex items-center justify-center mb-4 z-10" data-id="element-468">
                {step.id}
              </div>
              {/* Step content */}
              <div className="text-center" data-id="element-469">
                <div className="bg-white rounded-full p-4 shadow-sm border border-gray-100 mb-4 inline-flex" data-id="element-470">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'h-8 w-8 text-[#2563EB]'
              })}
                </div>
                <h3 className="font-bold text-lg mb-2" data-id="element-471">{step.title}</h3>
                <p className="text-gray-600" data-id="element-472">{step.description}</p>
              </div>
            </div>)}
        </div>
        {/* Mobile view - vertical steps */}
        <div className="md:hidden" data-id="element-473">
          <div className="flex overflow-x-auto pb-8 space-x-6 snap-x" data-id="element-474">
            {steps.map(step => <div key={step.id} className="snap-start scroll-ml-4 flex-shrink-0 w-64 p-4 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-475">
                <div className="flex items-center mb-4" data-id="element-476">
                  <div className="w-8 h-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center mr-3 text-sm" data-id="element-477">
                    {step.id}
                  </div>
                  <h3 className="font-bold" data-id="element-478">{step.title}</h3>
                </div>
                <div className="bg-blue-50 rounded-full p-3 inline-flex mb-3" data-id="element-479">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'h-6 w-6 text-[#2563EB]'
              })}
                </div>
                <p className="text-gray-600 text-sm" data-id="element-480">{step.description}</p>
              </div>)}
          </div>
          {/* Scroll indicator */}
          <div className="flex justify-center mt-4 space-x-2" data-id="element-481">
            {steps.map(step => <div key={step.id} className="w-2 h-2 rounded-full bg-gray-300" data-id="element-482"></div>)}
          </div>
        </div>
        {/* Visual installation guides */}
        <div className="mt-20" data-id="element-483">
          <div className="text-center mb-12" data-id="element-484">
            <h2 className="text-2xl font-bold mb-4" data-id="element-485">eSIM Installation Guide</h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-486">
              Follow these step-by-step visual instructions to install your eSIM
              on your device.
            </p>
          </div>
          {/* Device selector tabs */}
          <div className="flex justify-center mb-10" data-id="element-487">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1" data-id="element-488">
              <button className="px-4 py-2 rounded-md bg-[#2563EB] text-white font-medium flex items-center" data-id="element-489">
                <AppleIcon className="w-4 h-4 mr-2" data-id="element-490" />
                iPhone
              </button>
              <button className="px-4 py-2 rounded-md text-gray-600 font-medium flex items-center" data-id="element-491">
                <SmartphoneIcon className="w-4 h-4 mr-2" data-id="element-492" />
                Android
              </button>
            </div>
          </div>
          {/* iPhone installation steps */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-493">
            {/* Left column - iPhone mockup */}
            <div className="flex justify-center items-center" data-id="element-494">
              <div className="relative w-[280px] h-[560px]" data-id="element-495">
                {/* Phone frame */}
                <div className="absolute inset-0 bg-black rounded-[40px] shadow-xl overflow-hidden" data-id="element-496">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-36 h-6 bg-black rounded-b-xl z-10" data-id="element-497"></div>
                  {/* Screen */}
                  <div className="absolute top-0 left-0 right-0 bottom-0 m-2 bg-[#f2f2f7] rounded-[36px] overflow-hidden" data-id="element-498">
                    {/* Settings screen mockup */}
                    <div className="w-full h-full flex flex-col" data-id="element-499">
                      {/* Status bar */}
                      <div className="h-10 bg-[#f2f2f7] flex justify-between items-center px-5 pt-2" data-id="element-500">
                        <span className="text-xs font-semibold" data-id="element-501">9:41</span>
                        <div className="flex items-center space-x-1" data-id="element-502">
                          <div className="w-4 h-4 bg-black rounded-full" data-id="element-503"></div>
                          <div className="w-4 h-4 bg-black rounded-full" data-id="element-504"></div>
                          <div className="w-4 h-4 bg-black rounded-full" data-id="element-505"></div>
                        </div>
                      </div>
                      {/* Settings content */}
                      <div className="flex-1 bg-[#f2f2f7] px-4 py-2" data-id="element-506">
                        <div className="text-center mb-6" data-id="element-507">
                          <h3 className="text-lg font-semibold" data-id="element-508">Settings</h3>
                        </div>
                        {/* Settings options */}
                        <div className="bg-white rounded-xl overflow-hidden shadow-sm" data-id="element-509">
                          <div className="px-4 py-3 border-b border-gray-100 flex items-center" data-id="element-510">
                            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center mr-3" data-id="element-511">
                              <SmartphoneIcon className="w-5 h-5 text-white" data-id="element-512" />
                            </div>
                            <div data-id="element-513">
                              <p className="font-medium" data-id="element-514">Mobile Data</p>
                            </div>
                            <ArrowRightIcon className="w-4 h-4 text-gray-400 ml-auto" data-id="element-515" />
                          </div>
                          <div className="px-4 py-3 border-b border-gray-100 flex items-center bg-blue-50" data-id="element-516">
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center mr-3" data-id="element-517">
                              <QrCodeIcon className="w-5 h-5 text-white" data-id="element-518" />
                            </div>
                            <div data-id="element-519">
                              <p className="font-medium" data-id="element-520">Add eSIM</p>
                            </div>
                            <ArrowRightIcon className="w-4 h-4 text-gray-400 ml-auto" data-id="element-521" />
                          </div>
                          <div className="px-4 py-3 flex items-center" data-id="element-522">
                            <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center mr-3" data-id="element-523">
                              <WifiIcon className="w-5 h-5 text-white" data-id="element-524" />
                            </div>
                            <div data-id="element-525">
                              <p className="font-medium" data-id="element-526">Wi-Fi</p>
                            </div>
                            <ArrowRightIcon className="w-4 h-4 text-gray-400 ml-auto" data-id="element-527" />
                          </div>
                        </div>
                        {/* QR scanner overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center" data-id="element-528">
                          <div className="w-48 h-48 border-2 border-white rounded-lg relative" data-id="element-529">
                            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-400" data-id="element-530"></div>
                            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-400" data-id="element-531"></div>
                            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-400" data-id="element-532"></div>
                            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-400" data-id="element-533"></div>
                          </div>
                          <p className="text-white mt-4 text-sm" data-id="element-534">
                            Scan QR Code
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Right column - steps */}
            <div data-id="element-535">
              <h3 className="text-xl font-bold mb-6 text-[#2563EB]" data-id="element-536">
                iPhone eSIM Installation
              </h3>
              <div className="space-y-6" data-id="element-537">
                {iphoneSteps.map(step => <div key={step.id} className="flex bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-id="element-538">
                    <div className="w-10 h-10 rounded-full bg-[#2563EB] text-white flex items-center justify-center mr-4 flex-shrink-0" data-id="element-539">
                      {step.id}
                    </div>
                    <div className="flex-1" data-id="element-540">
                      <h4 className="font-semibold text-lg mb-1" data-id="element-541">
                        {step.title}
                      </h4>
                      <p className="text-gray-600 mb-3" data-id="element-542">{step.description}</p>
                      <div className="flex items-center text-[#2563EB] text-sm font-medium" data-id="element-543">
                        {step.icon}
                        <span className="ml-2" data-id="element-544">
                          Settings &gt; Cellular &gt; Add Cellular Plan
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Android installation preview (hidden by default) */}
          <div className="hidden grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-545">
            {/* Similar structure to iPhone section, would be shown when Android tab is selected */}
          </div>
        </div>
        {/* Additional information */}
        <div className="mt-16 bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-546">
          <h3 className="font-bold text-xl mb-4" data-id="element-547">Need more information?</h3>
          <p className="text-gray-600 mb-6" data-id="element-548">
            Our detailed installation guides are available for all major device
            manufacturers. If you need any assistance, our support team is
            available 24/7.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-549">
            <div className="border border-gray-200 rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer" data-id="element-550">
              <div className="flex items-center mb-2" data-id="element-551">
                <AppleIcon className="w-5 h-5 mr-2 text-[#2563EB]" data-id="element-552" />
                <h4 className="font-semibold" data-id="element-553">iPhone Installation Guide</h4>
              </div>
              <p className="text-sm text-gray-600" data-id="element-554">
                Step-by-step instructions for iPhone users
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 hover:bg-blue-50 transition-colors cursor-pointer" data-id="element-555">
              <div className="flex items-center mb-2" data-id="element-556">
                <SmartphoneIcon className="w-5 h-5 mr-2 text-[#2563EB]" data-id="element-557" />
                <h4 className="font-semibold" data-id="element-558">Android Installation Guide</h4>
              </div>
              <p className="text-sm text-gray-600" data-id="element-559">
                Step-by-step instructions for Android users
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}